import {Component, inject, OnInit} from '@angular/core';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {Card} from '../../../../../../shared/components/common/card/card';
import {PageHeaderComponent} from '../../../../../../shared/components/common/page-header/page-header.component';
import {LabelComponent} from '../../../../../../shared/components/form/label/label.component';
import {CreationButtons} from '../../../../../../shared/components/ui/creation-buttons/creation-buttons';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {ProspectosService} from '../prospectos.service';
import {ErrorService} from '../../../../../../shared/services/error.service';
import {SnackbarService} from '@shared/services/snackbar.service';
import {CombosService} from '../../../../../../shared/services/combos.service';
import {DocumentoTipo, GenericCombo} from '../../../../../../shared/services/interfaces/combos.interface';
import {forkJoin} from 'rxjs';
import {SelectFieldComponent, SelectOption} from '@shared/components/form/select/select-field.component';
import {TextareaFieldComponent} from '@shared/components/form/textarea/textarea-field.component';
import {InputFieldComponent} from '@shared/components/form/input/input-field.component';
import { AppFormComponent } from '@shared/components/ui/list-components';

@Component({
  selector: 'app-crear-prospecto',
  imports: [
    HasPermissionDirective,
    Card,
    PageHeaderComponent,
    LabelComponent,
    CreationButtons,
    ReactiveFormsModule,
    SelectFieldComponent,
    TextareaFieldComponent,
    InputFieldComponent,
    AppFormComponent,
  ],
  templateUrl: './crear-prospecto.html'
})
export class CrearProspecto implements OnInit {

  saveForm!: FormGroup;
  loading = false;
  canalesCaptacion: GenericCombo[] = [];
  documentosTipos: DocumentoTipo[] = [];

  tipoOptions: SelectOption[] = [
    { value: 'F', label: 'Física' },
    { value: 'J', label: 'Jurídica' },
  ];

  get canalesCaptacionOptions(): SelectOption[] {
    return this.canalesCaptacion.map(c => ({ value: c.id, label: c.nombre }));
  }

  get documentosTiposOptions(): SelectOption[] {
    return this.documentosTipos.map(d => ({ value: d.id, label: d.name }));
  }

  private fB = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private prospectosService = inject(ProspectosService);
  private combosService = inject(CombosService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);

  ngOnInit(): void {
    this.getCombos();
    this.makeForm();
  }

  private getCombos(): void {
    const canalesCaptacion = this.combosService.getCanalesCaptacionCombo();
    const documentosTipos = this.combosService.getDocumentoTipoCombo();

    forkJoin([canalesCaptacion, documentosTipos]).subscribe({
      next: (response) => {
        this.canalesCaptacion = response[0].data;
        this.documentosTipos = response[1].data;
        this.setDefaultDocumentoTipo();
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  private makeForm(): void {
    this.saveForm = this.fB.group({
      param_canal_captacion_id: ['', Validators.required],
      fecha_solicitud: [new Date().toISOString().split('T')[0]],
      nombre: ['', Validators.required],
      apellido: ['', Validators.required],
      documento: ['', Validators.required],
      per_documento_tipo_id: ['', Validators.required],
      nombre_empresa: '',
      tipo: ['F', Validators.required],
      telefono: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      whatsapp: '',
      descripcion: ''
    });
    this.updateTipoValidators();
    this.setDefaultDocumentoTipo();
  }

  private updateTipoValidators(): void {
    const nombreControl = this.saveForm.get('nombre');
    const apellidoControl = this.saveForm.get('apellido');
    const nombreEmpresaControl = this.saveForm.get('nombre_empresa');

    this.saveForm.get('tipo')?.valueChanges.subscribe((value) => {
      // Si el tipo seleccionado es 'F' (Físico), los campos nombre y apellido serán requeridos
      if (value === 'F') {
        nombreControl?.setValidators(Validators.required);
        apellidoControl?.setValidators(Validators.required);

        nombreEmpresaControl?.clearValidators();
      } else {
        // Si no es 'F', va a ser 'J' (Jurídico), solo el campo nombre_empresa será requerido
        nombreControl?.clearValidators();
        apellidoControl?.clearValidators();

        nombreEmpresaControl?.setValidators(Validators.required);
      }

      nombreControl?.updateValueAndValidity();
      apellidoControl?.updateValueAndValidity();
      nombreEmpresaControl?.updateValueAndValidity();
      this.setDefaultDocumentoTipo();
    });
  }

  private setDefaultDocumentoTipo(): void {
    if (!this.saveForm) {
      return;
    }

    const documentoTipoControl = this.saveForm?.get('per_documento_tipo_id');
    if (!documentoTipoControl || documentoTipoControl.value || !this.documentosTipos.length) {
      return;
    }

    const tipo = this.saveForm.get('tipo')?.value;
    const codigo = tipo === 'J' ? 'DOC_TIP_002' : 'DOC_TIP_001';
    const documentoTipo = this.documentosTipos.find((doc) => doc.email === codigo);
    if (documentoTipo) {
      documentoTipoControl.setValue(documentoTipo.id);
    }
  }

  saveProspecto(): void {
    this.loading = true;
    const payload = this.preparePayload();

    this.prospectosService.store(payload).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Prospecto creado correctamente');
          this.router.navigate(['../', response.data.id], {relativeTo: this.route});
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  private preparePayload(): any {
    const formValue = {...this.saveForm.value};

    if (formValue.tipo === 'F') {
      // Para personas físicas, eliminar nombre_empresa del payload
      delete formValue.nombre_empresa;
    } else {
      // Para personas jurídicas, eliminar nombre y apellido del payload
      delete formValue.nombre;
      delete formValue.apellido;
    }

    return formValue;
  }

  goBack(): void {
    this.router.navigate(['../'], {relativeTo: this.route});
  }

}
